import React from "react";

import { isEmpty } from "lodash/fp";

import Divider from "@mui/material/Divider";

import ApplyToSeries from "@app/analytics/components/ApplyToSeries";
import Tag from "@app/analytics/components/Tag";

import Stack, { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import SelectableTag from "./SelectableTag";
import {
  ActionButton,
  AdornedTextField,
  TagList,
  TagListContainer,
} from "./styles";
import TagInput from "./TagInput";
import type { UseManageTagsArgs, UseManageTagsReturn } from "./useManageTags";
import { isNewSuggestedTag } from "./utils";

type ManageTagsProps = Omit<
  UseManageTagsReturn,
  "onRemoveAll" | "removeAllDisabled"
> &
  Pick<UseManageTagsArgs, "canApplyToSeries"> & {
    onClose: () => void;
    loading?: boolean;
  };

const ManageTags = ({
  appliedTags,
  applyToSeries,
  availableTags,
  canApplyToSeries,
  focusInput,
  inputRef,
  inputValue,
  loading = false,
  onClose,
  onInputChange,
  onInputKeyDown,
  onRemoveTag,
  onSave,
  onSelectExistingTag,
  onSelectSuggestedTag,
  onTagColorChange,
  onToggleApplyToSeries,
  reset,
  suggestedTags,
}: ManageTagsProps) => {
  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <>
      <AdornedTextField onClick={focusInput} autoFocus>
        {appliedTags.map((tag) => (
          <Tag
            sizing="padded"
            key={tag.id}
            tag={tag}
            onAction={onRemoveTag(tag)}
          />
        ))}
        <TagInput
          ref={inputRef}
          onChange={onInputChange}
          value={inputValue}
          onKeyDown={onInputKeyDown}
        />
      </AdornedTextField>
      <TagListContainer spacing={1} sx={{ height: "126px", overflowY: "auto" }}>
        {!isEmpty(suggestedTags) && (
          <Stack>
            {suggestedTags.map((suggestedTag) => {
              const isNewTag = isNewSuggestedTag(suggestedTag, appliedTags);
              return (
                <SelectableTag
                  key={suggestedTag.id}
                  {...suggestedTag}
                  isNew={isNewTag}
                  onColorChange={
                    isNewTag ? onTagColorChange(suggestedTag.id) : undefined
                  }
                  onClick={() => onSelectSuggestedTag(suggestedTag)}
                />
              );
            })}
            <Divider />
          </Stack>
        )}
        <TagList>
          {availableTags.map((tag) => (
            <Tag
              key={tag.id}
              tag={tag}
              onClick={() => onSelectExistingTag(tag)}
            />
          ))}
        </TagList>
      </TagListContainer>
      {canApplyToSeries && (
        <Row variant="apart">
          <ApplyToSeries
            checked={applyToSeries}
            onChange={onToggleApplyToSeries}
          />
          <TooltipIcon
            title="Applies selected tags to all past and future meetings for this recurring event."
            placement="top-end"
          />
        </Row>
      )}
      <Row spacing={1}>
        <ActionButton onClick={handleClose}>Cancel</ActionButton>
        <ActionButton loading={loading} onClick={onSave} variant="contained">
          Save
        </ActionButton>
      </Row>
    </>
  );
};

export default ManageTags;
